import {
  Badge,
  Button,
  Card,
  Group,
  SegmentedControl,
  Select,
  SimpleGrid,
  Stack,
  Table,
  Text,
  Title,
} from "@mantine/core";
import { modals } from "@mantine/modals";
import { notifications } from "@mantine/notifications";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { ADMIN_LIST_PAGE_SIZE, AdminApiError, adminGet, adminSend } from "../api.ts";
import { AdminTable } from "../components/AdminTable.tsx";

type OutboxStatus = "pending" | "in_progress" | "succeeded" | "failed" | "rejected";

// WG 平台已知错误码 -> 运营友好中文。仅在前端展示用，权威定义不在此（payout-workflow.ts:51
// RETRYABLE_TAGS 决定是否重试）。新增码请同步评估是否进 RETRYABLE_TAGS。
const WG_ERROR_CODES: Record<number, string> = {
  1116000009: "WG 登录过期（运营在 admin 重新登录该 bot 的支付实例即可）",
  1116000030:
    "WG 需人工审核（金额触发风控 / 用户账号待核 等，运营在 WG 后台审核通过后系统不再重试此单）",
};

// 把后端落库的 last_attempt_error / final_failure_reason（形如
// "ProviderError: {\"provider\":\"wg\",\"code\":1116000030}"）解析出 WG code，
// 若在 WG_ERROR_CODES 表内则替换为中文释义并附原始 code；解析失败原样返回。
const renderFailureReason = (raw: string | null): string => {
  if (raw === null || raw === "") return "-";
  const m = /ProviderError:\s*(\{.*\})/.exec(raw);
  if (m !== null && m[1] !== undefined) {
    try {
      const parsed = JSON.parse(m[1]) as { provider?: string; code?: number; message?: string };
      if (typeof parsed.code === "number" && WG_ERROR_CODES[parsed.code] !== undefined) {
        return `${WG_ERROR_CODES[parsed.code]}（WG code=${parsed.code}）`;
      }
    } catch {
      // ignore，回落原文
    }
  }
  return raw;
};

interface OutboxRow {
  readonly id: string;
  readonly botId: string;
  readonly spinId: string;
  readonly paymentInstanceId: string | null;
  readonly childSiteCode: string | null;
  readonly platformUserIdx: number;
  readonly platformAccount: string;
  readonly amount: string;
  readonly currency: string;
  readonly remark: string;
  readonly status: OutboxStatus;
  readonly providerOrderId: string | null;
  readonly attempts: number;
  readonly lastError: string | null;
  readonly failureReason: string | null;
  readonly createdAt: string;
  readonly succeededAt: string | null;
  readonly failedAt: string | null;
  readonly nextAttemptAt: string | null;
}

interface OutboxListResponse {
  readonly records: ReadonlyArray<OutboxRow>;
  readonly limit: number;
  readonly offset: number;
}

const STATUS_COLOR: Record<OutboxStatus, string> = {
  pending: "gray",
  in_progress: "blue",
  succeeded: "green",
  failed: "red",
  rejected: "grape",
};

// rejected = WG 人工审核驳回（内部终态，不重试）。
const STATUS_LABEL: Record<OutboxStatus, string> = {
  pending: "待处理",
  in_progress: "处理中",
  succeeded: "成功",
  failed: "失败",
  rejected: "已驳回",
};

const fetchOutbox = (
  status: string,
  botFilter: string | null,
  offset: number,
): Promise<OutboxListResponse> => {
  const params = new URLSearchParams();
  if (status !== "all") params.set("status", status);
  if (botFilter !== null) params.set("botId", botFilter);
  params.set("limit", String(ADMIN_LIST_PAGE_SIZE));
  params.set("offset", String(offset));
  const qs = params.toString();
  return adminGet<OutboxListResponse>(`/grant-outbox${qs ? `?${qs}` : ""}`);
};

const retryOutbox = (id: string) => adminSend<void>("POST", `/grant-outbox/${id}/retry`);
const abortOutbox = (id: string) => adminSend<void>("POST", `/grant-outbox/${id}/abort`);

export const GrantOutboxPage = () => {
  const qc = useQueryClient();
  const [statusFilter, setStatusFilter] = useState("all");
  const [botFilter, setBotFilter] = useState<string | null>(null);
  const [offset, setOffset] = useState(0);

  const botsQuery = useQuery({
    queryKey: ["bots-list"],
    queryFn: () => adminGet<{ items: Array<{ id: string; name: string; code: string }> }>("/bots"),
  });

  const botSelectData =
    botsQuery.data?.items.map((b) => ({
      label: `${b.name} (${b.code})`,
      value: b.id,
    })) ?? [];

  const { data, isLoading } = useQuery({
    queryKey: ["grant-outbox", statusFilter, botFilter, offset],
    queryFn: () => fetchOutbox(statusFilter, botFilter, offset),
    staleTime: 15_000,
  });

  // 派彩汇总：默认今天按状态聚合，供运营对账资金流出。
  const summaryQuery = useQuery({
    queryKey: ["grant-outbox-summary", botFilter],
    queryFn: () => {
      const p = new URLSearchParams();
      if (botFilter !== null) p.set("botId", botFilter);
      p.set("days", "1");
      return adminGet<{
        days: number;
        items: ReadonlyArray<{ status: OutboxStatus; count: number; total: number }>;
      }>(`/grant-outbox/summary?${p.toString()}`);
    },
    refetchInterval: 30_000,
  });
  const summaryItems = summaryQuery.data?.items ?? [];

  const retryMut = useMutation({
    mutationFn: (id: string) => retryOutbox(id),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["grant-outbox"] });
      notifications.show({ message: "已重新入队", color: "green" });
    },
    onError: (e: AdminApiError) => notifications.show({ message: e.message, color: "red" }),
  });

  const abortMut = useMutation({
    mutationFn: (id: string) => abortOutbox(id),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["grant-outbox"] });
      notifications.show({ message: "已强制终止", color: "orange" });
    },
    onError: (e: AdminApiError) => notifications.show({ message: e.message, color: "red" }),
  });

  const records = data?.records ?? [];

  return (
    <Stack gap="md">
      <Title order={2}>派彩队列 (grant_outbox)</Title>

      <Group align="flex-end" gap="md">
        <SegmentedControl
          value={statusFilter}
          onChange={(v) => {
            setStatusFilter(v);
            setOffset(0);
          }}
          data={[
            { label: "全部", value: "all" },
            { label: "待处理", value: "pending" },
            { label: "处理中", value: "in_progress" },
            { label: "成功", value: "succeeded" },
            { label: "失败", value: "failed" },
            { label: "已驳回", value: "rejected" },
          ]}
        />
        <Select
          placeholder="按 Bot 筛选"
          searchable
          clearable
          data={botSelectData}
          value={botFilter}
          onChange={(v) => {
            setBotFilter(v);
            setOffset(0);
          }}
          style={{ minWidth: 200 }}
        />
      </Group>

      <SimpleGrid cols={{ base: 2, sm: 5 }} spacing="sm">
        {(["succeeded", "failed", "rejected", "pending", "in_progress"] as OutboxStatus[]).map(
          (s) => {
            const it = summaryItems.find((i) => i.status === s);
            return (
              <Card key={s} withBorder padding="sm">
                <Text size="xs" c="dimmed">
                  {STATUS_LABEL[s]}（今天）
                </Text>
                <Text fw={700} size="lg">
                  {it?.count ?? 0} 笔
                </Text>
                <Text size="xs" c="dimmed">
                  金额 {(it?.total ?? 0).toLocaleString()}
                </Text>
              </Card>
            );
          },
        )}
      </SimpleGrid>

      <Card withBorder>
        {isLoading ? (
          <Text>加载中...</Text>
        ) : records.length === 0 ? (
          <Text c="dimmed">无记录。</Text>
        ) : (
          <AdminTable minWidth={800}>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>会员</Table.Th>
                <Table.Th>实例 / 子站</Table.Th>
                <Table.Th>金额</Table.Th>
                <Table.Th>状态</Table.Th>
                <Table.Th>尝试</Table.Th>
                <Table.Th>订单号</Table.Th>
                <Table.Th>失败原因</Table.Th>
                <Table.Th>创建时间</Table.Th>
                <Table.Th>下次重试</Table.Th>
                <Table.Th>操作</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {records.map((r) => (
                <Table.Tr key={r.id}>
                  <Table.Td>
                    <Text size="sm">{r.platformAccount}</Text>
                    <Text size="xs" c="dimmed">
                      {r.platformUserIdx}
                    </Text>
                  </Table.Td>
                  <Table.Td>
                    <Text size="xs">{r.paymentInstanceId ?? "-"}</Text>
                    <Text size="xs" c="dimmed">
                      {r.childSiteCode ?? "-"}
                    </Text>
                  </Table.Td>
                  <Table.Td>
                    {r.amount} {r.currency}
                  </Table.Td>
                  <Table.Td>
                    <Badge color={STATUS_COLOR[r.status]}>
                      {STATUS_LABEL[r.status] ?? r.status}
                    </Badge>
                  </Table.Td>
                  <Table.Td>{r.attempts}</Table.Td>
                  <Table.Td>
                    <Text size="xs">{r.providerOrderId ?? "-"}</Text>
                  </Table.Td>
                  <Table.Td>
                    <Text size="xs" c="red" title={r.failureReason ?? r.lastError ?? ""}>
                      {renderFailureReason(r.failureReason ?? r.lastError)}
                    </Text>
                  </Table.Td>
                  <Table.Td>
                    <Text size="xs">{new Date(r.createdAt).toLocaleString()}</Text>
                  </Table.Td>
                  <Table.Td>
                    <Text size="xs">
                      {r.nextAttemptAt !== null ? new Date(r.nextAttemptAt).toLocaleString() : "-"}
                    </Text>
                  </Table.Td>
                  <Table.Td>
                    <Group gap="xs">
                      {r.status === "failed" ? (
                        <Button
                          size="xs"
                          variant="light"
                          loading={retryMut.isPending}
                          onClick={() => {
                            modals.openConfirmModal({
                              title: "确认重试",
                              children: "确认重新入队该派彩任务？",
                              labels: { confirm: "确认", cancel: "取消" },
                              onConfirm: () => retryMut.mutate(r.id),
                            });
                          }}
                        >
                          重试
                        </Button>
                      ) : null}
                      {r.status === "in_progress" ? (
                        <Button
                          size="xs"
                          variant="light"
                          color="red"
                          loading={abortMut.isPending}
                          onClick={() => {
                            modals.openConfirmModal({
                              title: "确认强制终止",
                              children: "该操作将把任务状态设为 failed，不可撤销。确认继续？",
                              labels: { confirm: "强制终止", cancel: "取消" },
                              confirmProps: { color: "red" },
                              onConfirm: () => abortMut.mutate(r.id),
                            });
                          }}
                        >
                          强制终止
                        </Button>
                      ) : null}
                      {r.status !== "failed" && r.status !== "in_progress" ? (
                        <Text size="xs" c="dimmed">
                          -
                        </Text>
                      ) : null}
                    </Group>
                  </Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </AdminTable>
        )}
      </Card>

      <Group justify="flex-end" gap="sm">
        <Button
          variant="default"
          size="sm"
          disabled={offset === 0}
          onClick={() => setOffset((prev) => Math.max(0, prev - ADMIN_LIST_PAGE_SIZE))}
        >
          上一页
        </Button>
        <Button
          variant="default"
          size="sm"
          disabled={records.length < ADMIN_LIST_PAGE_SIZE}
          onClick={() => setOffset((prev) => prev + ADMIN_LIST_PAGE_SIZE)}
        >
          下一页
        </Button>
      </Group>
    </Stack>
  );
};
